import { useQuery } from "@apollo/client";
import Errors from "components/base/Errors";
import Spinner from "components/base/Spinner";
import Status from "components/base/Status";
import useAutoScrollToBottom from "hooks/useAutoScrollToBottom";
import { FETCH_CHINA_V2_PRODUCT_STOCKS } from "./graphql";

const ODOO_COMPANY_NAMES = {
  0: "SS",
  1: "AB",
  3: "LTD",
};
const RECORD_FILTERS = {
  ALL: "all",
  SUPPLIER_STOCK: "supplier",
};
const SUPPLIER_TYPES = new Set(["PO", "SS", "PLAN"]);
const normalizeForSearch = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/gi, "");

const formatChangeQty = (value) => {
  if (!value) return null;
  const text = String(value);
  const number = Number(value);
  const withSign =
    Number.isFinite(number) && number > 0 && !text.startsWith("+")
      ? `+${text}`
      : text;
  return `(${withSign}) `;
};
const isNegativeNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) && num < 0;
};

const ProductStockHistoryTableView = ({ productId, supplierId, filter, q }) => {
  const { loading, error, data } = useQuery(FETCH_CHINA_V2_PRODUCT_STOCKS, {
    variables: { productId, supplierId },
    fetchPolicy:
      process.env.NODE_ENV === "development" ? "cache-first" : "network-only",
  });
  const containerRef = useAutoScrollToBottom(data);

  if (loading)
    return (
      <div className="mx-6 relative p-20 card">
        <Spinner />
      </div>
    );
  if (error) return <Errors error={error} />;

  const rows = JSON.parse(data.stockSupplierProductHistory);
  const normalizedQ = normalizeForSearch(q);
  const typeFilteredRows =
    filter === RECORD_FILTERS.SUPPLIER_STOCK
      ? rows.filter((row) => SUPPLIER_TYPES.has(String(row.type || "").trim()))
      : rows;
  const filteredRows = typeFilteredRows.filter((row) => {
    if (!normalizedQ) return true;
    return normalizeForSearch(row.origin).includes(normalizedQ);
  });
  const lastRow = rows.length > 0 ? rows[rows.length - 1] : null;

  console.log("rows", rows);

  return (
    <div className="mx-6 flex-1 card p-0 overflow-auto" ref={containerRef}>
      <table className="whitespace-nowrap font-code with-line">
        <thead>
          <tr className="sticky bg-gray-50/50 dark:bg-gray-800/50 backdrop-blur-lg top-0 z-20">
            <th className="py-3 px-4 sticky left-0 z-10 bg-gray-50/50 dark:bg-gray-800/50">
              Date
            </th>
            <th className="text-right">Odoo AB</th>
            <th className="text-right">Odoo LTD</th>
            <th className="text-right">Supplier Stock</th>
          </tr>
        </thead>
        <tbody>
          {filteredRows.map((i, index) => (
            <tr
              key={index}
              className={`hover:bg-blue-50 hover:dark:bg-blue-900/50 transition-all duration-100 hover:bg-blend-multiply`}
            >
              <td className="px-4">
                <div className="flex items-center space-x-2">
                  <div className="w-20">{i.date}</div>
                  <div className="w-12">{ODOO_COMPANY_NAMES[i.company_id]}</div>
                  <div className="flex justify-between flex-1">
                    <span className="text-xs opacity-70">
                      [{i.type}] {i.origin}
                    </span>
                    <Status status={i.state?.toUpperCase()} inline />
                  </div>
                </div>
              </td>
              <td className="text-right px-4">
                {formatChangeQty(i.odoo_qty)}
                {i.ab_cumsum}
              </td>
              <td className="text-right px-4">
                {formatChangeQty(i.odoo_ltd_qty)}
                {i.ltd_cumsum}
              </td>
              <td className="text-right px-4">
                {formatChangeQty(i.ss_qty)}
                <span
                  className={
                    isNegativeNumber(i.ss_cumsum)
                      ? "text-red-500 dark:text-red-400"
                      : ""
                  }
                >
                  {i.ss_cumsum}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
        {lastRow && (
          <tfoot>
            <tr className="sticky bottom-0 bg-gray-100/50 dark:bg-gray-800/50 backdrop-blur-lg z-10 font-semibold text-base">
              <td className="px-4 py-3 sticky left-0 z-20">Final Cumulative</td>
              <td className="text-right px-4">{lastRow["ab_cumsum"]}</td>
              <td className="text-right px-4">{lastRow["ltd_cumsum"]}</td>
              <td className="text-right px-4">
                <span
                  className={
                    isNegativeNumber(lastRow["ss_cumsum"])
                      ? "text-red-500 dark:text-red-400"
                      : ""
                  }
                >
                  {lastRow["ss_cumsum"]}
                </span>
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
};

export default ProductStockHistoryTableView;
